import React, { useEffect, useMemo, useCallback, useState } from 'react';
import { Select, SelectItem, Selection, Input } from '@nextui-org/react';
import { MarketData, SettlementDetails } from 'common/src/utils/types';

import useDataStore from '../../stores/useDataStore';
import { useTranslation } from '../../hooks/useTranslation';

interface VarietyFilterProps {
  data: MarketData[] | SettlementDetails[];
  selectedVariety: string | string[];
  onSelectionChange: (value: string[]) => void;
  selectionMode?: 'single' | 'multiple';
  label?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  defaultSelectedKeys?: string[];
  hideAllOption?: boolean;
  allowAllVarieties?: boolean;
  defaultSelectAll?: boolean;
  topN?: number; // Número de variedades top a seleccionar por defecto
  showCounts?: boolean; // Mostrar contadores de registros
}

const VarietyFilter: React.FC<VarietyFilterProps> = ({
  data,
  selectedVariety,
  onSelectionChange,
  selectionMode = 'single',
  label,
  className = 'min-w-32',
  size = 'sm',
  defaultSelectedKeys,
  hideAllOption = false,
  allowAllVarieties = false,
  defaultSelectAll = false,
  topN = 5,
  showCounts = false,
}) => {
  const { t } = useTranslation('common');
  const { selectedSpecies } = useDataStore();

  // Use translated label or fallback
  const finalLabel = label || t('Variedad');
  const [search, setSearch] = useState('');

  // Usar una ref para evitar re-renders innecesarios
  const userDeselectedAllRef = React.useRef(false);

  // Ref para controlar si ya se hizo una selección automática
  const hasAutoSelectedRef = React.useRef(false);

  const setUserDeselectedAll = (value: boolean) => {
    userDeselectedAllRef.current = value;
  };

  // Memoizar la lista de variedades permitidas
  const allowedVarietiesList = useMemo(() => {
    if (allowAllVarieties) {
      return [];
    } else if (
      selectedSpecies?.varieties &&
      selectedSpecies.varieties.length > 0
    ) {
      return selectedSpecies.varieties.map((v) => v.toLowerCase());
    }
    return [];
  }, [allowAllVarieties, selectedSpecies?.varieties]);

  // Memoizar la lista de variedades con sus recuentos
  const varietiesWithCounts = useMemo(() => {
    // Create a map to count records per variety
    const varietyCount = data.reduce((acc, item) => {
      const variety = item?.variety?.toLowerCase();
      if (
        variety &&
        (allowedVarietiesList.length === 0 || // Use all varieties if empty list
          allowedVarietiesList.includes(variety))
      ) {
        acc[variety] = (acc[variety] || 0) + 1;
      }
      return acc;
    }, {} as Record<string, number>);

    // Convert to array and sort by count
    return Object.entries(varietyCount).sort(
      ([, countA], [, countB]) => countB - countA
    );
  }, [data, allowedVarietiesList]);

  // Memoizar la lista de variedades ordenadas
  const varieties = useMemo(() => {
    return varietiesWithCounts.map(([variety]) => variety?.toUpperCase());
  }, [varietiesWithCounts]);

  const filteredVarieties = useMemo(() => {
    if (!search) return varieties;
    const lower = search.toLowerCase();
    return varieties.filter((v) => v.toLowerCase().includes(lower));
  }, [varieties, search]);

  // Memoizar las top N variedades (las que tienen más registros)
  const topNVarieties = useMemo(() => {
    return varieties.slice(0, topN);
  }, [varieties, topN]);

  // Memoizar la variedad más común (con más registros)
  const mostCommonVariety = useMemo(() => {
    if (varietiesWithCounts.length > 0) {
      return varietiesWithCounts[0][0]?.toUpperCase();
    }
    return '';
  }, [varietiesWithCounts]);

  // Verificar cuando cambian los datos para potencialmente reiniciar la selección
  useEffect(() => {
    // Si los datos cambian significativamente, permitir una nueva selección automática
    if (data.length > 0 && varieties.length > 0) {
      hasAutoSelectedRef.current = false;
    }
  }, [data, varieties]);

  // Memoizar los selected keys
  const selectedKeys = useMemo(() => {
    if (selectionMode === 'multiple') {
      return Array.isArray(selectedVariety)
        ? new Set(selectedVariety)
        : new Set([]);
    }
    return new Set([selectedVariety as string]);
  }, [selectionMode, selectedVariety]);

  // Memoizar el texto para la opción "Todas"
  const allOptionText = useMemo(() => {
    if (selectionMode === 'multiple') {
      return Array.isArray(selectedVariety) && selectedVariety.length > 0
        ? t('Deseleccionar todos')
        : t('Seleccionar todos');
    }
    return t('Todos');
  }, [selectionMode, selectedVariety, t]);

  // Memoizar el handler de selección
  const handleSelectionChange = useCallback(
    (keys: Selection) => {
      if (selectionMode === 'multiple') {
        const keysArray = Array.from(keys);

        if (keysArray.includes('all')) {
          // If 'all' is selected and there were previous selections, clear all
          if (Array.isArray(selectedVariety) && selectedVariety.length > 0) {
            onSelectionChange([]);
            setUserDeselectedAll(true); // User has explicitly deselected all
          } else {
            // Otherwise, select all varieties
            onSelectionChange(varieties);
            setUserDeselectedAll(false);
          }
        } else {
          // Normal selection behavior for individual items
          onSelectionChange(keysArray as string[]);
          setUserDeselectedAll(false);
        }
      } else {
        // Keep existing behavior for single selection
        const selectedKeys = Array.from(keys);
        onSelectionChange(selectedKeys as string[]);
        setUserDeselectedAll(false);
        // Marcar como seleccionado explícitamente
        hasAutoSelectedRef.current = true;
      }
    },
    [selectionMode, selectedVariety, varieties, onSelectionChange]
  );

  // Effect para selección automática inicial
  useEffect(() => {
    // Solo hacer selección automática si no se ha hecho antes
    if (hasAutoSelectedRef.current) return;

    // Only execute if there are available varieties
    if (varieties.length === 0) return;

    // No hacer nada si ya hay una selección válida o hay valores por defecto
    if (
      (Array.isArray(selectedVariety) && selectedVariety.length > 0) ||
      (typeof selectedVariety === 'string' &&
        selectedVariety !== '' &&
        selectedVariety !== 'all' &&
        varieties.includes(selectedVariety))
    ) {
      hasAutoSelectedRef.current = true;
      return;
    }

    // Si es multiple y defaultSelectAll está activado, seleccionar todas
    if (selectionMode === 'multiple' && defaultSelectAll) {
      onSelectionChange(varieties);
      hasAutoSelectedRef.current = true;
      return;
    }

    // Si el usuario no ha deseleccionado explícitamente todo
    if (!userDeselectedAllRef.current) {
      // Si es modo múltiple y topN está definido, seleccionar las top N variedades
      if (selectionMode === 'multiple' && topN > 0 && topNVarieties.length > 0) {
        onSelectionChange(topNVarieties);
        hasAutoSelectedRef.current = true;
      }
      // Seleccionar la variedad más común (la primera de la lista ordenada)
      else if (mostCommonVariety) {
        onSelectionChange([mostCommonVariety]);
        hasAutoSelectedRef.current = true;
      } else if (varieties.length > 0) {
        // Como fallback, seleccionar la primera variedad
        onSelectionChange([varieties[0]]);
        hasAutoSelectedRef.current = true;
      }
    }
  }, [
    varieties,
    topNVarieties,
    mostCommonVariety,
    selectedVariety,
    selectionMode,
    defaultSelectAll,
    topN,
    onSelectionChange,
    userDeselectedAllRef,
  ]);

  // Si no hay variedades, no renderizar nada
  if (varieties.length === 0) {
    return null;
  }

  // Renderizar elementos SelectItem para el componente
  const renderSelectItems = () => {
    const items = [];

    // Agregar opción "Todas" si corresponde
    if (selectionMode === 'single' && !hideAllOption) {
      items.push(
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          {t('Todos')}
        </SelectItem>
      );
    }

    if (selectionMode === 'multiple' && !hideAllOption) {
      items.push(
        <SelectItem key='all' value='all' textValue={t('Todos')}>
          <div className='flex justify-between items-center'>
            <span>{allOptionText}</span>
          </div>
        </SelectItem>
      );
    }

    // Agregar items para cada variedad
    const counts = Object.fromEntries(varietiesWithCounts);
    filteredVarieties.forEach((variety) => {
      const count = counts[variety.toLowerCase()] || 0;
      items.push(
        <SelectItem key={variety} value={variety} textValue={variety}>
          {showCounts ? (
            <div className="flex justify-between items-center w-full">
              <span>{variety}</span>
              <span className="text-xs text-default-400 ml-2">({count})</span>
            </div>
          ) : (
            variety
          )}
        </SelectItem>
      );
    });

    return items;
  };

  return (
    <Select
      label={finalLabel}
      selectedKeys={selectedKeys}
      onSelectionChange={handleSelectionChange}
      selectionMode={selectionMode}
      defaultSelectedKeys={defaultSelectedKeys && new Set(defaultSelectedKeys)}
      className={className}
      size={size}
      aria-label={`${t('Selecciona variedad')}`}
      renderValue={(items) => {
        if (
          selectionMode === 'multiple' &&
          Array.isArray(selectedVariety) &&
          selectedVariety.length === varieties.length
        ) {
          return (
            <div className='flex items-center gap-2'>{t('Todas las variedades')}</div>
          );
        }
        if (Array.isArray(selectedVariety) && selectedVariety.length > 0) {
          return (
            <div className='flex items-center gap-2'>
              {selectedVariety.join(', ')}
            </div>
          );
        }
        return (
            <div className='flex items-center gap-2'>
              {items.map((item) => item.textValue).join(', ')}
            </div>
          );
      }}
      listboxProps={{
        topContent: (
          <div className='sticky top-0 z-10 bg-content1 px-1 pt-1 pb-1'>
            <Input
              size='sm'
              placeholder={t('Buscar variedad...')}
              value={search}
              onValueChange={setSearch}
              isClearable
              onClear={() => setSearch('')}
            />
          </div>
        ),
      }}
    >
      {renderSelectItems()}
    </Select>
  );
};

// Usar una función de comparación personalizada para React.memo
export default React.memo(VarietyFilter, (prevProps, nextProps) => {
  // Siempre re-renderizar si cambian estos props importantes
  if (
    prevProps.selectionMode !== nextProps.selectionMode ||
    prevProps.allowAllVarieties !== nextProps.allowAllVarieties ||
    prevProps.hideAllOption !== nextProps.hideAllOption
  ) {
    return false;
  }

  // Comparación eficiente de arrays
  const prevSelected = Array.isArray(prevProps.selectedVariety)
    ? prevProps.selectedVariety.join(',')
    : prevProps.selectedVariety;
  const nextSelected = Array.isArray(nextProps.selectedVariety)
    ? nextProps.selectedVariety.join(',')
    : nextProps.selectedVariety;

  if (prevSelected !== nextSelected) {
    return false;
  }

  // Verificar si los datos han cambiado realmente (comparación superficial)
  if (prevProps.data.length !== nextProps.data.length) {
    return false;
  }

  // Si la función de callback cambia, también re-renderizar
  if (prevProps.onSelectionChange !== nextProps.onSelectionChange) {
    return false;
  }

  // Si llegamos aquí, no ha habido cambios significativos
  return true;
});
